import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import html2canvas from "html2canvas";
import PurOrderDetailPage from "./PurOrderDetailPage";
import AddPurOrderPage from "./AddPurOrderPage";
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit, XCircle, Printer, Send } from "lucide-react";

/* ------------------ Print Preview Modal ------------------ */
const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = React.useRef(null);

  const handlePrint = () => {
    const iframe = document.getElementById("print-iframe");
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleWhatsAppShare = async () => {
    if (!hiddenRef.current) {
      alert("Invoice content not ready.");
      return;
    }

    const countryCode = "91";
    const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
    if (!mobileNumber) {
      alert("Mobile number not available for WhatsApp share.");
      return;
    }
    const fullNumber = countryCode + mobileNumber;

    try {
      const canvas = await html2canvas(hiddenRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "image/png": blob,
          }),
        ]);
        alert("Invoice copied to clipboard! Now you can paste it in WhatsApp.");
      }
    } catch (err) {
      console.warn("Clipboard copy failed or not supported:", err);
    }

    const message = encodeURIComponent("Please find attached invoice.");
    window.open(`https://wa.me/${fullNumber}?text=${message}`, "_blank");
  };

  return (
    <>
      <div
        ref={hiddenRef}
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          width: "800px",
          backgroundColor: "white",
          padding: "10px",
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-sm p-4 bg-white rounded-lg shadow-2xl">
          <h3 className="mb-2 text-lg font-semibold text-center">
            Invoice Preview
          </h3>
          <div className="border border-gray-300">
            <iframe
              id="print-iframe"
              srcDoc={htmlContent}
              title="Print Preview"
              style={{ width: "100%", height: "60vh", border: "none" }}
            />
          </div>

          <div className="flex justify-end gap-7 mt-2">
            <Button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
               hover:from-red-600 hover:to-red-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <XCircle size={16} />
              Close
            </Button>

            <Button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
               hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Printer size={16} />
              Print
            </Button>

            <Button
              onClick={handleWhatsAppShare}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md 
               hover:from-green-600 hover:to-emerald-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Send size={16} />
              WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ------------------ Order Card ------------------ */
const OrderCard = ({ order, onEdit, onPrintInvoice }) => {
  return (
    <div className="p-4 transition-shadow bg-white border border-gray-200 shadow rounded-xl hover:shadow-lg">
      <h3 className="text-base font-semibold text-gray-900">{order.Vname}</h3>
      <div className="mt-2 text-sm text-gray-700"># {order.Poid}</div>
      <div className="text-sm text-gray-700">
        {order.Podate ? new Date(order.Podate).toLocaleDateString() : ""}
      </div>
      <div className="text-sm text-gray-900 font-semibold">
        {order.InvNo ? order.InvNo : ""}
      </div>
      <div className="mt-1 text-sm text-gray-700">
        {order.SumQty} / {order.Payment} {order.OrderTotal}
      </div>

      <div className="flex gap-3 mt-4 flex-nowrap">
        <Button
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded shadow 
             hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
          onClick={onEdit}
        >
          <FileEdit className="w-5 h-5" />
          Edit
        </Button>

        <Button
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-green-500 rounded shadow 
             hover:bg-green-600 transition-all duration-200"
          onClick={onPrintInvoice}
        >
          Invoice
        </Button>
      </div>
    </div>
  );
};

/* ------------------ Main Page ------------------ */
const PurchaseOrderListPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [printContent, setPrintContent] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);

  const [view, setView] = useState("list"); // list | addpur-order | editpur-order
  const [editingContext, setEditingContext] = useState(null);
  const [viewMode, setViewMode] = useState("card"); // 'card' | 'table'

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getpurSimplePOList(user.UsrName);
      const list = (data?.Table || []).map((o) => ({
        ...o,
        cpid: Number(o.cpid) || 0,
      }));
      setOrders(list);
      setFilteredOrders(list);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch orders.");
    } finally {
      setLoading(false);
    }
  }, [user.UsrName]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const term = (searchTerm || "").toLowerCase();
    const results = orders.filter(
      (o) =>
        (o.Vname && o.Vname.toLowerCase().includes(term)) ||
        (o.MobileNo && o.MobileNo.includes(searchTerm))
    );
    setFilteredOrders(results);
  }, [searchTerm, orders]);

  const handlePrintInvoice = async (order) => {
    try {
      const resp = await api.MobGetpurfinalizeOrder(order.Poid);

      const content =
        resp?.Table?.[0]?.PrintContent ||
        resp?.Table3?.[0]?.PrintContent ||
        resp?.Table4?.[0]?.PrintContent ||
        resp?.MobGetpurfinalizeOrderjs?.Table?.[0]?.PrintContent ||
        resp?.data?.MobGetpurfinalizeOrderjs?.Table?.[0]?.PrintContent ||
        resp?.data?.Table?.[0]?.PrintContent ||
        resp?.data?.Table4?.[0]?.PrintContent ||
        null;

      if (content) {
        setPrintContent(content);
        setCurrentOrder(order);

        const match = content.match(/Bill No -\s*([^*<]+)/i);
        const newInvNo = match ? match[1].trim() : null;

        if (newInvNo) {
          setOrders((prev) =>
            prev.map((o) =>
              o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o
            )
          );
          setFilteredOrders((prev) =>
            prev.map((o) =>
              o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o
            )
          );
        }
      } else {
        alert("Print content not found. Please check API response.");
      }
    } catch (err) {
      console.error("Error fetching print content:", err);
      alert("Error fetching print content.");
    }
  };

  // --- Switch Views ---
  if (view === "editpur-order" && editingContext) {
    return (
      <PurOrderDetailPage
        context={editingContext}
        setView={setView}
        refreshOrders={fetchOrders}
      />
    );
  }

  if (view === "addpur-order") {
    return (
      <AddPurOrderPage
        setView={setView}
        setEditingContext={setEditingContext}
        refreshOrders={fetchOrders}
      />
    );
  }

  return (
    <div className="p-4">
      {printContent && currentOrder && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}

      <Heading>Purchase Order</Heading>

      <div className="flex items-center justify-between mb-6 gap-4">
        {/* New Button */}
        <Button
          className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
            rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
          onClick={() => setView("addpur-order")}
        >
          <PlusCircle size={18} />
          <span className="font-medium">New</span>
        </Button>

        {/* Icon toggle buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("card")}
            className={`p-2 rounded ${viewMode === "card"
                ? "bg-white text-blue-600 shadow"
                : "text-gray-500"
              }`}
          >
            {/* Grid Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z"
              />
            </svg>
          </button>

          <button
            onClick={() => setViewMode("table")}
            className={`p-2 rounded ${viewMode === "table"
                ? "bg-white text-blue-600 shadow"
                : "text-gray-500"
              }`}
          >
            {/* Table/List Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {/* Back Button */}
        <Button
          variant="outline"
          className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
          onClick={() => setView("home")}
        >
          ⬅
        </Button>


        <SearchInput
          placeholder="Search by vendor or mobile..."
          className="flex-1"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <Spinner />
      ) : error ? (
        <MessageBox message={error} />
      ) : filteredOrders.length === 0 ? (
        <div className="py-12 text-center text-gray-500">No orders found.</div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.Poid}
              order={order}
              onEdit={() => {
                setEditingContext({
                  id: order.Poid,
                  customerId: order.Vid,
                  orderDate: order.Podate,
                });
                setView("editpur-order");
              }}
              onPrintInvoice={() => handlePrintInvoice(order)}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-gray-900 text-sm">
                  PO ID
                </th>
                <th className="px-4 py-2 text-left text-gray-900 text-sm">
                  Vendor
                </th>
                <th className="px-4 py-2 text-left text-gray-900 text-sm">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-gray-900 text-sm">
                  Invoice
                </th>
                <th className="px-4 py-2 text-left text-gray-900 text-sm">
                  Qty / Payment
                </th>
                <th className="px-4 py-2 text-center text-gray-900 text-sm">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredOrders.map((order) => (
                <tr key={order.Poid}>
                  <td className="px-4 py-2">{order.Poid}</td>
                  <td className="px-4 py-2">{order.Vname}</td>
                  <td className="px-4 py-2">
                    {order.Podate
                      ? new Date(order.Podate).toLocaleDateString()
                      : ""}
                  </td>
                  <td className="px-4 py-2">{order.InvNo || ""}</td>
                  <td className="px-4 py-2">
                    {order.SumQty} / {order.Payment} {order.OrderTotal}
                  </td>
                  <td className="px-4 py-2 text-center flex justify-center gap-1">
                    <Button
                      className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                      onClick={() => {
                        setEditingContext({
                          id: order.Poid,
                          customerId: order.Vid,
                          orderDate: order.Podate,
                        });
                        setView("editpur-order");
                      }}
                    >
                      <FileEdit className="w-5 h-5" />
                      <span className="font-medium">Edit</span>
                    </Button>
                    <Button
                      className="px-3 py-1.5 mt-1 text-xs text-white bg-green-500 rounded hover:bg-green-600"
                      onClick={() => handlePrintInvoice(order)}
                    >
                      Invoice
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderListPage;

